import { FormControl, Grid } from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import SelectErp from "../../basics/SelectErp";
import { GlobalContext } from "../../../contexts/GlobalContext";
import {
  getAccountantClients,
  getEntityDataById,
} from "../../../repositories/portal_api/AccountantClient";
import DocumentGuideManagerFields from "./DocumentGuideManagerFields";
import BodyFinancialMovement from "./BodyFinancialMovement";
import BodyNotIdentified from "./BodyNotIdentified";
import {
  getDocumentTypeMovementType,
  getFolderDataOfDocumentType,
  getRelatedEntityOfDocumentType,
} from "../utils/DocumentDataConverter";

const BodyEditDocumentData = ({
  documentData,
  listDocuments,
  documentIndex,
  setListDocuments,
  documentTypes,
}) => {
  const [documentType, setDocumentType] = useState(null);
  const [documentSubType, setDocumentSubType] = useState(null);

  const [supplierDocument, setSupplierDocument] = useState(
    documentData.relatedEntity.document
  );
  const [supplierName, setSupplierName] = useState(
    documentData.relatedEntity.name
  );

  const [customerData, setCustomerData] = useState(documentData.customerData);
  const [listCustomers, setListCustomers] = useState([]);

  //DOCUMENT GUIDE MANAGEMENT
  const [folderSystem, setFolderSystem] = useState("guide");
  const [folderType, setFolderType] = useState("report");
  const [sendTo, setSendTo] = useState(null);

  const [disabledSubtype, setDisabledSubtype] = useState(true);
  const [documentSubtypeList, setDocumentSubtypeList] = useState([]);

  const [disabledSupplier, setDisabledSupplier] = useState(false);

  const { companyUser } = useContext(GlobalContext);

  useEffect(() => {
    const getCustomers = async () => {
      const response = await getAccountantClients();
      const currentCompanyResponse = await getEntityDataById(
        companyUser.companyId
      );

      if (response?.data) {
        setListCustomers([currentCompanyResponse.data, ...response.data]);
      } else {
        setListCustomers([currentCompanyResponse.data]);
      }
    };
    getCustomers();
  }, [companyUser.companyId]);

  useEffect(() => {
    const initialSetup = async () => {
      const currentDocumentType = documentTypes.find(
        (type) => type.name === documentData.documentType
      );

      setDocumentType(currentDocumentType);

      if (currentDocumentType?.subtypes?.length > 0) {
        setDocumentSubType(currentDocumentType.subtypes[0]);
      }

      if (documentData.movementType === "PAYABLE") {
        setSupplierDocument(documentData.relatedEntity.document);
        setSupplierName(documentData.relatedEntity.name);

        const loggedCompany = listCustomers.find(
          (customer) => customer.id === companyUser.companyId
        );
        if (loggedCompany) {
          setSendTo(loggedCompany);
        }
      } else {
        if (documentData.relatedEntity.document) {
          const customer = listCustomers.find(
            (customer) =>
              customer.document === documentData.relatedEntity.document
          );
          if (customer) {
            setCustomerData(customer);
            setSendTo(customer);
          }
        }
      }

      setFolderSystem(documentData.folderSystem);
      setFolderType(documentData.folderType);
    };
    initialSetup();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (documentType === null) {
      setDisabledSubtype(true);
      setDocumentSubtypeList([]);
    } else {
      if (
        documentTypes.find((type) => type.id === documentType.id).subtypes
          ?.length > 0
      ) {
        setDocumentSubtypeList(
          documentTypes.find((type) => type.id === documentType.id).subtypes
        );
        setDisabledSubtype(false);
      } else {
        setDocumentSubtypeList([]);
        setDisabledSubtype(true);
      }
    }

    if (documentType) {
      const movementType = getDocumentTypeMovementType(documentType?.name);

      if (movementType) {
        let netAmount = 0;
        let grossAmount = 0;
        let fineAmount = 0;
        let interestAmount = 0;
        let discountAmount = 0;

        if (documentData?.netAmount) {
          netAmount = documentData?.netAmount;
        }
        if (documentData?.grossAmount) {
          grossAmount = documentData?.grossAmount;
        }
        if (documentData?.fineAmount) {
          fineAmount = documentData?.fineAmount;
        }
        if (documentData?.interestAmount) {
          interestAmount = documentData?.interestAmount;
        }
        if (documentData?.discountAmount) {
          discountAmount = documentData?.discountAmount;
        }

        changeDocumentData({
          ...documentData,
          documentType: documentType?.name,
          movementType: getDocumentTypeMovementType(documentType?.name),
          netAmount: netAmount,
          grossAmount: grossAmount,
          fineAmount: fineAmount,
          interestAmount: interestAmount,
          discountAmount: discountAmount,
        });
      } else {
        changeDocumentData({
          ...documentData,
          documentType: documentType.name,
          folderSystem: folderSystem,
          folderType: folderType,
        });
      }

      const relatedEntityData = getRelatedEntityOfDocumentType(documentType);
      const folderData = getFolderDataOfDocumentType(documentType);

      if (relatedEntityData) {
        setCustomerData(null);
        setSupplierDocument(relatedEntityData.document);
        setSupplierName(relatedEntityData.name);
        setDisabledSupplier(relatedEntityData.disabled);
      } else {
        if (!customerData) {
          setCustomerData(null);
        }
        if (!supplierDocument) {
          setSupplierDocument("");
        }
        if (!supplierName) {
          setSupplierName("");
        }
        setDisabledSupplier(false);
      }
      if (folderData) {
        setFolderSystem(folderData.folderSystem);
        setFolderType(folderData.folderType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType, documentTypes]);

  useEffect(() => {
    if (customerData) {
      changeDocumentData({
        ...documentData,
        relatedEntity: {
          name: customerData.company_name,
          document: customerData.document,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerData]);

  const changeDocumentData = (newDocumentData) => {
    const newListDocuments = [...listDocuments];

    newListDocuments[documentIndex] = newDocumentData;

    setListDocuments(newListDocuments);
  };

  useEffect(() => {
    if (sendTo) {
      changeDocumentData({
        ...documentData,
        sendTo: sendTo,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendTo]);

  useEffect(() => {
    if (folderType) {
      changeDocumentData({
        ...documentData,
        folderType: folderType,
        folderSystem: folderSystem,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderType, folderSystem]);

  return (
    <div style={{ padding: 8, paddingLeft: 16 }}>
      <Grid container spacing={2}>
        <Grid xs={12} style={{ marginTop: "10px" }}></Grid>
        <Grid item xs={6}>
          <FormControl size="small" fullWidth placeholder="Tipo de documento">
            <SelectErp
              variant="outlined"
              label="Tipo de documento"
              size="medium"
              value={documentType}
              setValue={setDocumentType}
              optionsList={documentTypes}
              descriptionField="exibition_name"
              getOptionLabel={(option) => {
                return option?.exibition_name || "";
              }}
              getOptionValue={(option) => {
                return option?.id || null;
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl size="small" fullWidth placeholder="Tipo de documento">
            <SelectErp
              variant="outlined"
              label="Subtipo de documento"
              size="medium"
              value={documentSubType}
              setValue={setDocumentSubType}
              disabled={disabledSubtype}
              optionsList={documentSubtypeList}
              descriptionField="exibition_name"
              getOptionLabel={(option) => {
                return option?.exibition_name || "";
              }}
              getOptionValue={(option) => {
                return option?.id || null;
              }}
            />
          </FormControl>
        </Grid>

        {documentType?.name === "OTHER" ? (
          <BodyNotIdentified
            documentData={documentData}
            listDocuments={listDocuments}
            setListDocuments={setListDocuments}
            documentIndex={documentIndex}
            changeDocumentData={changeDocumentData}
          ></BodyNotIdentified>
        ) : (
          <BodyFinancialMovement
            documentData={documentData}
            listCustomers={listCustomers}
            supplierName={supplierName}
            setSupplierName={setSupplierName}
            supplierDocument={supplierDocument}
            setSupplierDocument={setSupplierDocument}
            customerData={customerData}
            setCustomerData={setCustomerData}
            disabledSupplier={disabledSupplier}
            changeDocumentData={changeDocumentData}
          ></BodyFinancialMovement>
        )}

        <Grid item xs={12}>
          <Grid
            container
            justifyContent="flex-start"
            style={{ paddingLeft: 8 }}
          >
            <DocumentGuideManagerFields
              folderSystem={folderSystem}
              setFolderSystem={setFolderSystem}
              folderType={folderType}
              setFolderType={setFolderType}
              listCustomers={listCustomers}
              sendTo={sendTo}
              setSendTo={setSendTo}
            />
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default BodyEditDocumentData;
